
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, TranslationKey, translations } from '@/utils/i18n';

type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  // The t function accepts a key and returns the translated string
  t: (key: TranslationKey | string) => string;
  // The tp function for pluralization
  tp: (key: TranslationKey | string, count: number) => string;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

type LanguageProviderProps = {
  children: ReactNode;
};

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Check for user preference in localStorage
    const savedLanguage = localStorage.getItem('language') as Language | null;
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'fr')) return savedLanguage;
    
    // Check browser language
    const browserLang = navigator.language.split('-')[0];
    if (browserLang === 'fr') return 'fr';
    
    // Default to English
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  // This function gets a key and returns the translated string
  const t = (key: TranslationKey | string): string => {
    // Safety check for undefined or null keys
    if (!key) return '';

    try {
      const translation = translations[language][key];
      
      if (translation !== undefined) {
        if (typeof translation === 'string') {
          return translation;
        } else if (translation && typeof translation === 'object' && 'other' in translation) {
          // Default to 'other' form for plural objects when using t()
          return translation.other;
        }
      }
      
      // If translation doesn't exist, return the key as fallback
      // Remove the console.warn to avoid cluttering the console
      return key.split('.').pop() || key;
      
    } catch (error) {
      // Return the last part of the key as fallback (e.g., "sidebar.channels" -> "channels")
      return key.split('.').pop() || key;
    }
  };

  // Function for pluralization
  const tp = (key: TranslationKey | string, count: number): string => {
    try {
      const translation = translations[language][key];
      
      // If it's a string, just return it with {count} replaced
      if (typeof translation === 'string') {
        return translation.replace('{count}', count.toString());
      }
      
      // If it's a plural object, handle pluralization
      if (translation && typeof translation === 'object' && 'one' in translation) {
        let result = '';
        
        if (count === 0 && translation.zero) {
          result = translation.zero;
        } else if (count === 1) {
          result = translation.one;
        } else {
          result = translation.other;
        }
        
        // Replace {count} placeholder with the actual count
        return result.replace('{count}', count.toString());
      }
      
      // Fallback
      return key.split('.').pop() || key;
    } catch (error) {
      return key.split('.').pop() || key;
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, tp }}>
      {children}
    </LanguageContext.Provider>
  );
};
